// apps/client/src/pages/settings/components/AccountTypesConfig.tsx
//
// Lets the user pick which Xero account types should be included in the
// R&D claim. EXPENSE is always on; other types (ASSET, LIABILITY, EQUITY,
// REVENUE, BANK, DEPRECIATION) are opt-in. Drives the Transactions
// allocation page, Account rules, Reports summary, Project summary, and
// the Excel R&D export.

import { AccountType } from '@avanzu/types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { apiClient } from '../../../services/apiClient';

interface AccountTypesConfigProps {
  organisationId: string;
  initialAllowed: AccountType[];
  disabled?: boolean;
  onSaved?: (next: AccountType[]) => void;
}

// Order matters here — EXPENSE first (locked), then the optional opt-ins.
const OPTIONAL_TYPES: { type: AccountType; label: string; help: string }[] = [
  { type: AccountType.ASSET, label: 'Asset', help: 'Capitalised purchases, prepayments.' },
  { type: AccountType.LIABILITY, label: 'Liability', help: 'Loan repayments, accrued costs.' },
  { type: AccountType.EQUITY, label: 'Equity', help: 'Owner contributions or drawings.' },
  { type: AccountType.REVENUE, label: 'Revenue', help: 'Income lines (e.g. grants, refunds).' },
  { type: AccountType.BANK, label: 'Bank', help: 'Transfers and bank-account journal lines.' },
  {
    type: AccountType.DEPRECIATION,
    label: 'Depreciation',
    help: 'Depreciation journals against assets.',
  },
];

const AccountTypesConfig: React.FC<AccountTypesConfigProps> = ({
  organisationId,
  initialAllowed,
  disabled,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selected, setSelected] = useState<Set<AccountType>>(
    () => new Set(initialAllowed.length ? initialAllowed : [AccountType.EXPENSE])
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialSet = useMemo(
    () => new Set(initialAllowed.length ? initialAllowed : [AccountType.EXPENSE]),
    [initialAllowed]
  );

  const isDirty = useMemo(() => {
    if (selected.size !== initialSet.size) return true;
    const arr = Array.from(selected);
    for (const t of arr) if (!initialSet.has(t)) return true;
    return false;
  }, [selected, initialSet]);

  const toggle = (t: AccountType) => {
    setError(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Always include EXPENSE — the backend enforces this too, but make
      // sure we send a sane payload.
      const payload = Array.from(new Set([...Array.from(selected), AccountType.EXPENSE]));
      const response = await apiClient.put(
        `/organisations/${organisationId}/settings/allowed-account-types`,
        { allowedAccountTypes: payload }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}) as any);
        if (response.status === 409 && data?.code === 'allocations_exist') {
          setError(data.error);
          return;
        }
        setError(data?.error || 'Failed to save account types');
        return;
      }

      const data = await response.json();
      const saved: AccountType[] = data?.allowedAccountTypes ?? payload;
      enqueueSnackbar('Account types updated', { variant: 'success' });
      onSaved?.(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save account types');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setError(null);
    setSelected(new Set(initialSet));
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          R&D account types
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose which Xero account types appear on the Transactions allocation page,
          Account rules, Transaction Summary, and the Excel export. <strong>EXPENSE</strong>{' '}
          is always included.
        </Typography>

        <Stack spacing={1}>
          <FormControlLabel
            control={<Checkbox checked disabled />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Expense
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Always included — core R&D expenditure.
                </Typography>
              </Box>
            }
          />
          {OPTIONAL_TYPES.map(({ type, label, help }) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  checked={selected.has(type)}
                  onChange={() => toggle(type)}
                  disabled={saving || disabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {help}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 3 }} justifyContent="flex-end">
          <Button onClick={handleReset} disabled={!isDirty || saving || disabled}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isDirty || saving || disabled}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AccountTypesConfig;
